package com.heelion.farmafreelance.ui

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.material3.TabRowDefaults.tabIndicatorOffset
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import farmafreelance.composeapp.generated.resources.*
import org.jetbrains.compose.resources.stringResource
import com.heelion.farmafreelance.models.*
import com.heelion.farmafreelance.utils.TimeUtils
import kotlinx.datetime.*
import kotlinx.coroutines.launch

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun StoricoScreen(viewModel: AuthViewModel, onBack: () -> Unit) {
    var selectedTab by remember { mutableStateOf(0) }
    var storicoList by remember { mutableStateOf<List<StoricoEntry>>(emptyList()) }
    var fattureList by remember { mutableStateOf<List<FatturaEntry>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    val scope = rememberCoroutineScope()
    
    val themeColorHex = viewModel.selectedThemeColor.hex.removePrefix("0x").toLong(16)
    val themeColor = Color(themeColorHex)
    
    // Supporto per selezione multipla
    val selectedShifts = remember { mutableStateListOf<StoricoEntry>() }

    LaunchedEffect(selectedTab) {
        isLoading = true
        if (selectedTab == 0) {
            storicoList = viewModel.getStorico()
        } else {
            fattureList = viewModel.getStoricoFatture()
        }
        isLoading = false
    }

    Scaffold(
        containerColor = Color.Transparent,
        topBar = {
            TopAppBar(
                colors = TopAppBarDefaults.topAppBarColors(containerColor = Color.Transparent),
                title = { Text("") }, // Titolo spostato nella dashboard
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        Icon(AppIcons.ArrowBack, contentDescription = "Back")
                    }
                },
                actions = {
                    if (selectedShifts.isNotEmpty() && selectedTab == 0) {
                        TextButton(onClick = {
                            val firstPhId = selectedShifts.first().farmaciaId
                            val allSamePh = selectedShifts.all { it.farmaciaId == firstPhId }
                            
                            if (allSamePh) {
                                scope.launch {
                                    viewModel.creaFatturaProforma(selectedShifts.map { it.annuncioId }, firstPhId)
                                    selectedShifts.clear()
                                    selectedTab = 1
                                }
                            }
                        }) {
                            Text("GENERA AVVISO (${selectedShifts.size})", fontWeight = FontWeight.Bold, color = themeColor)
                        }
                    }
                }
            )
        }
    ) { padding ->
        Column(modifier = Modifier.fillMaxSize().padding(padding).padding(horizontal = 16.dp)) {
            // --- DASHBOARD STILE APP ---
            Card(
                modifier = Modifier.fillMaxWidth(),
                colors = CardDefaults.cardColors(
                    containerColor = if (themeColor == Color.White) Color.White.copy(alpha = 0.1f) else themeColor.copy(alpha = 0.08f)
                ),
                shape = RoundedCornerShape(16.dp),
                border = androidx.compose.foundation.BorderStroke(
                    width = 0.5.dp,
                    color = if (themeColor == Color.White) Color(0xFFC0C0C0) else themeColor.copy(alpha = 0.2f)
                ),
                elevation = CardDefaults.cardElevation(defaultElevation = 0.dp)
            ) {
                Column(modifier = Modifier.padding(16.dp)) {
                    Text(
                        text = stringResource(Res.string.storico_title),
                        style = MaterialTheme.typography.headlineMedium.copy(fontWeight = FontWeight.Bold),
                        color = when {
                            themeColor == Color.White -> Color.Black
                            themeColor == Color(0xFF2196F3) -> Color(0xFF0D47A1)
                            else -> themeColor
                        }
                    )
                }
            }

            Spacer(modifier = Modifier.height(24.dp))

            TabRow(
                selectedTabIndex = selectedTab, 
                containerColor = Color.Transparent,
                divider = {},
                indicator = { tabPositions ->
                    if (selectedTab < tabPositions.size) {
                        TabRowDefaults.SecondaryIndicator(
                            modifier = Modifier.tabIndicatorOffset(tabPositions[selectedTab]),
                            color = themeColor
                        )
                    }
                }
            ) {
                Tab(
                    selected = selectedTab == 0, 
                    onClick = { selectedTab = 0 }, 
                    text = { Text("Turni", fontSize = 16.sp, fontWeight = if (selectedTab == 0) FontWeight.Bold else FontWeight.Normal) },
                    selectedContentColor = themeColor,
                    unselectedContentColor = Color.Gray
                )
                Tab(
                    selected = selectedTab == 1, 
                    onClick = { selectedTab = 1 }, 
                    text = { Text("Avvisi", fontSize = 16.sp, fontWeight = if (selectedTab == 1) FontWeight.Bold else FontWeight.Normal) },
                    selectedContentColor = themeColor,
                    unselectedContentColor = Color.Gray
                )
            }

            Spacer(modifier = Modifier.height(16.dp))

            if (isLoading) {
                Box(modifier = Modifier.weight(1f).fillMaxWidth(), contentAlignment = Alignment.Center) {
                    CircularProgressIndicator(color = themeColor)
                }
            } else {
                Box(modifier = Modifier.weight(1f)) {
                    if (selectedTab == 0) {
                        if (storicoList.isEmpty()) {
                            EmptyStoricoView(AppIcons.Turno, "Nessun turno completato")
                        } else {
                            LazyColumn(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                                items(storicoList) { entry ->
                                    val isSelected = selectedShifts.any { it.id == entry.id }
                                    StoricoItem(
                                        entry = entry, 
                                        viewModel = viewModel,
                                        isSelected = isSelected,
                                        themeColor = themeColor,
                                        onToggleSelect = {
                                            if (isSelected) selectedShifts.removeAll { it.id == entry.id }
                                            else {
                                                if (selectedShifts.isEmpty() || selectedShifts.first().farmaciaId == entry.farmaciaId) {
                                                    selectedShifts.add(entry)
                                                }
                                            }
                                        }
                                    ) {
                                        scope.launch {
                                            viewModel.creaFatturaProforma(listOf(entry.annuncioId), entry.farmaciaId)
                                            selectedTab = 1
                                        }
                                    }
                                }
                            }
                        }
                    } else {
                        if (fattureList.isEmpty()) {
                            EmptyStoricoView(AppIcons.List, "Nessun avviso emesso")
                        } else {
                            LazyColumn(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                                items(fattureList) { fattura ->
                                    FatturaItem(fattura, themeColor)
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun EmptyStoricoView(icon: androidx.compose.ui.graphics.vector.ImageVector, text: String) {
    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
        Column(horizontalAlignment = Alignment.CenterHorizontally) {
            Icon(icon, contentDescription = null, tint = Color.LightGray, modifier = Modifier.size(64.dp))
            Spacer(Modifier.height(16.dp))
            Text(text, color = Color.Gray)
        }
    }
}

@Composable
fun StoricoItem(
    entry: StoricoEntry, 
    viewModel: AuthViewModel, 
    isSelected: Boolean = false,
    themeColor: Color = Color(0xFF2196F3),
    onToggleSelect: () -> Unit = {},
    onGenerateInvoice: () -> Unit
) {
    val userRole = viewModel.currentUserRole
    // Farmacista o infermiere: vede la farmacia, spunta i pagamenti ed emette gli avvisi.
    val isProfessionista = userRole?.isProfessionista == true
    Card(
        modifier = Modifier.fillMaxWidth().clickable { onToggleSelect() },
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp),
        colors = CardDefaults.cardColors(containerColor = if (isSelected) themeColor.copy(alpha = 0.05f) else Color.White),
        border = if (isSelected) androidx.compose.foundation.BorderStroke(2.dp, themeColor) else null,
        shape = RoundedCornerShape(12.dp)
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    if (isProfessionista) {
                        Checkbox(
                            checked = isSelected, 
                            onCheckedChange = { onToggleSelect() },
                            colors = CheckboxDefaults.colors(checkedColor = themeColor)
                        )
                        Spacer(Modifier.width(8.dp))
                    }

                    Text(
                        text = TimeUtils.formatDate(entry.dataTurno),
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold
                    )
                }
                Surface(
                    color = themeColor.copy(alpha = 0.1f),
                    shape = RoundedCornerShape(8.dp)
                ) {
                    val ore = TimeUtils.calculateHours(entry.oraInizio, entry.oraFine)
                    val totaleShift = ore * entry.compenso
                    
                    Column(modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp), horizontalAlignment = Alignment.End) {
                        Text(
                            text = "${TimeUtils.formatCurrency(totaleShift)} €",
                            style = MaterialTheme.typography.labelMedium,
                            fontWeight = FontWeight.ExtraBold,
                            color = themeColor
                        )
                        Text(
                            text = "(${TimeUtils.formatCurrency(entry.compenso)} €/h)",
                            style = MaterialTheme.typography.labelSmall,
                            fontSize = 8.sp,
                            color = Color.Gray
                        )
                    }
                }
            }
            
            Spacer(modifier = Modifier.height(8.dp))
            
            Text(
                text = "${entry.oraInizio} - ${entry.oraFine}",
                style = MaterialTheme.typography.bodySmall,
                color = Color.Gray
            )
            
            HorizontalDivider(modifier = Modifier.padding(vertical = 8.dp), thickness = 0.5.dp)
            
            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(
                    imageVector = if (isProfessionista) AppIcons.Farmacia else AppIcons.Farmacista,
                    contentDescription = null,
                    modifier = Modifier.size(16.dp),
                    tint = themeColor.copy(alpha = 0.7f)
                )
                Spacer(Modifier.width(8.dp))
                Text(
                    text = (if (isProfessionista) entry.nomeFarmacia
                        else entry.nomeFarmacista?.let { if (entry.professione == Professione.INFERMIERE) "$it (infermiere)" else it }) ?: "N/D",
                    style = MaterialTheme.typography.bodyMedium,
                    fontWeight = FontWeight.Medium
                )
            }

            if (isProfessionista) {
                Spacer(modifier = Modifier.height(12.dp))
                Button(
                    onClick = onGenerateInvoice,
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(8.dp),
                    colors = ButtonDefaults.buttonColors(containerColor = themeColor)
                ) {
                    Icon(AppIcons.List, contentDescription = null, modifier = Modifier.size(18.dp))
                    Spacer(Modifier.width(8.dp))
                    Text("GENERA AVVISO PAGAMENTO", fontSize = 11.sp, fontWeight = FontWeight.Bold)
                }
            }
        }
    }
}

@Composable
fun FatturaItem(fattura: FatturaEntry, themeColor: Color = Color(0xFF1976D2)) {
    val uriHandler = androidx.compose.ui.platform.LocalUriHandler.current
    Card(
        modifier = Modifier.fillMaxWidth(),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        shape = RoundedCornerShape(12.dp)
    ) {
        Row(modifier = Modifier.padding(16.dp), verticalAlignment = Alignment.CenterVertically) {
            Icon(AppIcons.List, contentDescription = null, tint = themeColor, modifier = Modifier.size(32.dp))
            Spacer(Modifier.width(16.dp))
            Column(modifier = Modifier.weight(1f)) {
                Text("Avviso n. ${fattura.numeroFattura}", fontWeight = FontWeight.Bold)
                if (fattura.nomeFarmacia != null) {
                    Text(fattura.nomeFarmacia!!, style = MaterialTheme.typography.bodySmall, fontWeight = FontWeight.Medium, color = Color.Gray)
                }
                if (fattura.dataTurno != null && fattura.orario != null) {
                    Text("Turno: ${TimeUtils.formatDate(fattura.dataTurno!!)} (${fattura.orario})", style = MaterialTheme.typography.labelSmall, color = themeColor.copy(alpha = 0.8f))
                }
                Text("Emissione: ${TimeUtils.formatDate(fattura.dataEmissione, TimeZone.currentSystemDefault())}", style = MaterialTheme.typography.labelSmall, color = Color.Gray)
                Text("Totale: ${TimeUtils.formatCurrency(fattura.importoTotale)} €", style = MaterialTheme.typography.bodySmall, fontWeight = FontWeight.Bold, color = themeColor)
            }
            IconButton(onClick = { 
                val lang = androidx.compose.ui.text.intl.Locale.current.language
                val url = "${com.heelion.farmafreelance.BASE_URL}/api/matches/fatture/download/${fattura.id}?lang=$lang${com.heelion.farmafreelance.api.FarmaFreelanceApi.parametroToken()}"
                uriHandler.openUri(url)
            }) {
                Icon(AppIcons.Search, contentDescription = "Apri", tint = themeColor)
            }
        }
    }
}
